import { useEffect, useState, useRef } from 'react';

interface UseTypewriterEffectOptions {
  /** Target text to display */
  text: string;
  /** Typing speed in characters per second (default: 100) */
  speed?: number;
  /** Whether to enable the effect (default: true) */
  enabled?: boolean;
}

/**
 * Custom hook that creates a typewriter effect for displaying text character-by-character.
 *
 * This hook is designed to work with streaming content from the backend:
 * - Backend sends content chunks every ~100ms via SSE
 * - This hook smoothly displays characters at configurable speed (default 100 chars/sec)
 * - Handles retry scenarios where content is cleared and starts over
 * - Resumes from last position when new content arrives
 *
 * @example
 * ```tsx
 * const { content, status } = useLetterGeneration();
 * const displayedContent = useTypewriterEffect({
 *   text: content,
 *   speed: 100,
 *   enabled: status === 'streaming' || status === 'validating',
 * });
 * ```
 */
export const useTypewriterEffect = ({
  text,
  speed = 100, // 100 chars/sec is fast but still visibly smooth
  enabled = true,
}: UseTypewriterEffectOptions): string => {
  const [displayedText, setDisplayedText] = useState('');
  const displayedIndexRef = useRef(0);
  const animationFrameRef = useRef<number | null>(null);
  const lastUpdateTimeRef = useRef<number>(0);

  useEffect(() => {
    // If disabled, show full text immediately
    if (!enabled) {
      setDisplayedText(text);
      displayedIndexRef.current = text.length;
      return;
    }

    // If text is empty (retry scenario), reset everything
    if (text.length === 0) {
      setDisplayedText('');
      displayedIndexRef.current = 0;
      return;
    }

    // If text is shorter than what we've displayed (shouldn't happen but handle it)
    if (text.length < displayedIndexRef.current) {
      setDisplayedText(text);
      displayedIndexRef.current = text.length;
      return;
    }

    // If we're already showing all the text, nothing to do
    if (displayedIndexRef.current >= text.length) {
      return;
    }

    // Start animation loop
    const intervalMs = 1000 / speed; // Convert chars/sec to ms/char
    lastUpdateTimeRef.current = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - lastUpdateTimeRef.current;

      // Check if enough time has passed to show next character
      if (elapsed >= intervalMs) {
        const charsToAdd = Math.floor(elapsed / intervalMs);
        const newIndex = Math.min(
          displayedIndexRef.current + charsToAdd,
          text.length
        );

        if (newIndex > displayedIndexRef.current) {
          displayedIndexRef.current = newIndex;
          setDisplayedText(text.slice(0, newIndex));
          lastUpdateTimeRef.current = currentTime;
        }
      }

      // Continue animation if we haven't displayed all text yet
      if (displayedIndexRef.current < text.length) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    // Start animation
    animationFrameRef.current = requestAnimationFrame(animate);

    // Cleanup function
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [text, speed, enabled]);

  return displayedText;
};
